import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Mail, Linkedin, Sparkles, MessageSquare, Workflow, Puzzle, Hotel, UtensilsCrossed, Home as HomeIcon, Stethoscope, ShoppingBag, Briefcase, MapPin, CheckCircle2, Clock, TrendingDown, BarChart3, Store, Scale, Building2, Bot, Cpu, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import ContactModal from '../components/ContactModal';
import { useIsMobile } from '../hooks/useIsMobile';

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const heroRef = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });

  // Parallax transforms — desktop only. On mobile we render static values
  // so scroll doesn't trigger continuous style recalculations.
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div className="bg-white overflow-x-hidden">
      <Header onBookConsultation={() => setIsModalOpen(true)} />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={isMobile ? undefined : { opacity: heroOpacity }}
      >
        {/* Animated Background */}
        <motion.div
          className="absolute inset-0 z-0"
          style={isMobile ? undefined : { y: heroY }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50" />
          <motion.div
            className="absolute top-20 -left-20 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl"
            animate={isMobile ? undefined : {
              x: [0, 50, 0],
              y: [0, 30, 0],
              scale: [1, 1.1, 1]
            }}
            transition={isMobile ? undefined : { duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-[600px] h-[600px] bg-blue-300/20 rounded-full blur-3xl"
            animate={isMobile ? undefined : {
              x: [0, -30, 0],
              y: [0, 50, 0],
              scale: [1, 1.2, 1]
            }}
            transition={isMobile ? undefined : { duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center pt-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2, type: 'spring' }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-blue-200 shadow-sm mb-8"
            >
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-slate-700">Automatización e Inteligencia Artificial</span>
            </motion.div>
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-slate-900 mb-8 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <motion.span
              className="block overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <motion.span
                className="block"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: [0.33, 1, 0.68, 1] }}
              >
                Automatización e IA
              </motion.span>
            </motion.span>
            <motion.span
              className="block overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <motion.span
                className="block bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, delay: 0.8, ease: [0.33, 1, 0.68, 1] }}
              >
                para Empresas
              </motion.span>
            </motion.span>
            <motion.span
              className="block overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <motion.span
                className="block"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, delay: 1, ease: [0.33, 1, 0.68, 1] }}
              >
                que quieren crecer
              </motion.span>
            </motion.span>
          </motion.h1>

          <motion.p
            className="text-base sm:text-lg md:text-xl text-slate-600 mb-12 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            Ayudamos a empresas a ahorrar tiempo, reducir costes y mejorar su eficiencia mediante la automatización de procesos y agentes de inteligencia artificial.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                size="lg" 
                onClick={() => setIsModalOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-lg rounded-xl shadow-lg group"
              >
                Reserva una Consulta Gratuita
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-50 px-8 py-6 text-lg rounded-xl"
              >
                Ver Soluciones
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-slate-400 rounded-full p-1">
            <motion.div
              className="w-1.5 h-1.5 bg-slate-600 rounded-full mx-auto"
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </motion.section>

      {/* Marquee */}
      <MarqueeSection />

      {/* Trust Section */}
      <TrustSection />

      {/* Services Section */}
      <ServicesSection />

      {/* Why Choose Us */}
      <WhyChooseUsSection />

      {/* Process Section */}
      <ProcessSection />

      {/* Industries Section */}
      <IndustriesSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <CTASection onBookConsultation={() => setIsModalOpen(true)} />

      {/* Footer */}
      <Footer onBookConsultation={() => setIsModalOpen(true)} />
    </div>
  );
};

// Marquee Component — CSS-only animation (runs on the compositor, no JS per-frame)
const MarqueeSection = () => {
  return (
    <div className="py-8 bg-slate-900 overflow-hidden">
      <div className="flex whitespace-nowrap aivanilo-marquee-track">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-8 px-8">
            <span className="text-white/80 text-sm font-medium">AUTOMATIZACIÓN DE PROCESOS</span>
            <span className="text-blue-400">•</span>
            <span className="text-white/80 text-sm font-medium">AGENTES DE IA</span>
            <span className="text-blue-400">•</span>
            <span className="text-white/80 text-sm font-medium">INTEGRACIONES</span>
            <span className="text-blue-400">•</span>
            <span className="text-white/80 text-sm font-medium">AHORRO DE COSTES</span>
            <span className="text-blue-400">•</span>
            <span className="text-white/80 text-sm font-medium">ESCALABILIDAD</span>
            <span className="text-blue-400">•</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Trust Section Component
const TrustSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const pillars = [
    { icon: Clock, title: 'Ahorro de tiempo', description: 'Automatizamos las tareas repetitivas para que tu equipo se centre en lo que aporta valor.' },
    { icon: TrendingDown, title: 'Reducción de costes', description: 'Menos trabajo manual y menos errores se traducen en un ahorro real y medible.' },
    { icon: BarChart3, title: 'Escalabilidad', description: 'Soluciones que crecen con tu empresa, sin fricciones ni límites.' }
  ];

  return (
    <section ref={ref} className="py-20 md:py-32 px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50/30">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <p className="text-sm text-blue-600 font-semibold mb-4 tracking-wide uppercase">Tecnología para tu empresa</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Soluciones de IA para <span className="text-blue-600">empresas serias</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            Cada empresa tiene procesos distintos. Analizamos cómo trabajas y diseñamos automatizaciones e integraciones a medida que se adaptan a tu operativa, no al revés.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ y: -8 }}
              >
                <Card className="p-8 h-full border-2 border-slate-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 rounded-2xl bg-white">
                  <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{pillar.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{pillar.description}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    {
      icon: Bot,
      title: 'Agentes de Inteligencia Artificial',
      description: "Asistentes que atienden a tus clientes 24/7 por WhatsApp, web o email: resuelven dudas, cualifican leads y gestionan citas.",
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Workflow,
      title: 'Automatización de Procesos',
      description: 'Eliminamos las tareas repetitivas de tu día a día automatizando flujos completos entre tus herramientas.',
      color: 'from-blue-600 to-blue-700'
    },
    {
      icon: Puzzle,
      title: 'Integraciones',
      description: 'Conectamos tu CRM, ERP, email, mensajería y software de gestión para que la información fluya sin trabajo manual.',
      color: 'from-slate-700 to-slate-800'
    },
    {
      icon: Cpu,
      title: 'Soluciones de IA a Medida',
      description: 'Desarrollamos proyectos de inteligencia artificial adaptados a los procesos concretos de cada empresa.',
      color: 'from-blue-500 to-slate-700'
    }
  ];

  return (
    <section id="services" ref={ref} className="py-20 md:py-32 px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="text-sm text-blue-600 font-semibold mb-4 tracking-wide uppercase">Qué Hacemos</p>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-slate-900">Automatización e IA aplicada a tu negocio</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index, isInView }) => {
  const Icon = service.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="group"
    >
      <Card className="p-8 h-full border-2 border-slate-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 rounded-2xl bg-white">
        <motion.div
          className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
        >
          <Icon className="w-8 h-8 text-white" />
        </motion.div>
        <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
        <p className="text-slate-600 leading-relaxed">{service.description}</p>
      </Card>
    </motion.div>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const reasons = [
    { title: 'Especialistas en IA', description: 'Enfocados exclusivamente en automatización e inteligencia artificial aplicada a empresas.' },
    { title: 'Implementación rápida', description: 'La mayoría de automatizaciones se despliegan en cuestión de días, no de meses.' },
    { title: 'Soporte cercano', description: 'Comunicación directa, seguimiento y mejoras continuas de tus procesos.' },
    { title: 'Soluciones escalables', description: 'Cada automatización se diseña para crecer al ritmo de tu empresa.' }
  ];

  return (
    <section ref={ref} className="py-20 md:py-32 px-6 lg:px-8 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-4">Por qué las empresas nos eligen</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 360 }}
                transition={{ duration: 0.6 }}
                className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle2 className="w-8 h-8 text-white" />
              </motion.div>
              <h3 className="text-xl font-bold mb-3">{reason.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{reason.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const steps = [
    { number: '01', title: 'Consulta gratuita', description: 'Analizamos tu flujo de trabajo actual.' },
    { number: '02', title: 'Propuesta de automatización', description: 'Diseñamos la solución de IA ideal.' },
    { number: '03', title: 'Implementación', description: 'Construimos e integramos todo.' },
    { number: '04', title: 'Optimización continua', description: 'Mejoramos la automatización con el tiempo.' }
  ];

  return (
    <section id="process" ref={ref} className="py-20 md:py-32 px-6 lg:px-8 bg-gradient-to-b from-blue-50/30 to-white">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="text-sm text-blue-600 font-semibold mb-4 tracking-wide uppercase">Cómo Trabajamos</p>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-slate-900">Nuestro Proceso</h2>
        </motion.div>

        <div className="space-y-12 md:space-y-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="relative"
            >
              <div className="flex items-start gap-5 md:gap-8">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="flex-shrink-0 w-16 h-16 md:w-24 md:h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-2xl md:text-3xl font-bold text-white shadow-lg"
                >
                  {step.number}
                </motion.div>
                <div className="flex-1 pt-1 md:pt-2">
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 md:mb-3">{step.title}</h3>
                  <p className="text-base md:text-lg text-slate-600">{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <motion.div
                  initial={{ scaleY: 0 }}
                  animate={isInView ? { scaleY: 1 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.4 }}
                  className="absolute left-8 md:left-12 top-16 md:top-24 w-0.5 h-12 md:h-16 bg-gradient-to-b from-blue-500 to-blue-300 origin-top"
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const industries = [
    { icon: Hotel, name: 'Hoteles' },
    { icon: UtensilsCrossed, name: 'Restaurantes' },
    { icon: Stethoscope, name: 'Clínicas' },
    { icon: HomeIcon, name: 'Inmobiliarias' },
    { icon: Scale, name: 'Despachos' },
    { icon: Store, name: 'Tiendas' },
    { icon: Briefcase, name: 'Empresas de Servicios' },
    { icon: Building2, name: 'Pymes y Grandes Empresas' }
  ];

  return (
    <section id="industries" ref={ref} className="py-20 md:py-32 px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20 max-w-3xl mx-auto"
        >
          <p className="text-sm text-blue-600 font-semibold mb-4 tracking-wide uppercase">Con quién trabajamos</p>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-slate-900 mb-6">Adaptado a cualquier tipo de empresa</h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            La automatización y la IA aportan valor en cualquier sector. Estos son solo algunos ejemplos de empresas con las que trabajamos.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {industries.map((industry, index) => {
            const Icon = industry.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                whileHover={{ y: -8, scale: 1.05 }}
                className="group"
              >
                <Card className="p-6 md:p-8 text-center border-2 border-slate-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300 rounded-2xl h-full flex flex-col items-center justify-center">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-500 transition-colors duration-300"
                  >
                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </motion.div>
                  <p className="font-semibold text-slate-900 text-sm">{industry.name}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// CTA Section
// CTA Section
const CTASection = ({ onBookConsultation }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const isMobile = useIsMobile();

  return (
    <section ref={ref} className="py-20 md:py-32 px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
      {/* Animated Background Elements — animated on desktop, static on mobile */}
      <motion.div
        className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
        animate={isMobile ? undefined : { x: [0, 100, 0], y: [0, 50, 0] }}
        transition={isMobile ? undefined : { duration: 20, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl"
        animate={isMobile ? undefined : { x: [0, -100, 0], y: [0, -50, 0] }}
        transition={isMobile ? undefined : { duration: 25, repeat: Infinity }}
      />

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 md:mb-8">
            ¿Listo para automatizar
            <br />
            <span className="text-blue-400">tu empresa?</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-10 md:mb-12 max-w-2xl mx-auto">
            Cuéntanos cómo trabajas y te mostraremos qué procesos puedes automatizar para ahorrar tiempo y reducir costes.
          </p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="inline-block">
            <Button 
              size="lg" 
              onClick={onBookConsultation}
              className="bg-white text-slate-900 hover:bg-blue-50 px-10 md:px-12 py-6 md:py-8 text-lg md:text-xl rounded-xl shadow-2xl font-bold group"
            >
              Hablemos
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQItem = ({ item, isOpen, onToggle, index }) => {
  return (
    <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white transition-colors duration-200 hover:border-slate-300">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${index}`}
        id={`faq-btn-${index}`}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
      >
        <span className="text-base sm:text-lg font-semibold text-slate-900">{item.q}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="shrink-0 text-blue-600"
        >
          <ChevronDown className="w-5 h-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`faq-panel-${index}`}
            role="region"
            aria-labelledby={`faq-btn-${index}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-5 text-slate-600 leading-relaxed">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      q: '¿Qué tipo de procesos se pueden automatizar?',
      a: 'Cualquier tarea repetitiva y basada en reglas: gestión de correos, respuestas a clientes, entrada de datos, generación de documentos, sincronización entre herramientas, seguimiento de leads o reportes. Analizamos tu operativa y priorizamos lo que aporta más valor.',
    },
    {
      q: '¿Necesito conocimientos técnicos para trabajar con vosotros?',
      a: 'No. Nos encargamos del diseño, la implementación y el mantenimiento. Tú nos explicas cómo trabajas y nosotros nos ocupamos de la parte técnica, dejándote todo funcionando y documentado.',
    },
    {
      q: '¿Cuánto tiempo tarda una implementación?',
      a: 'Depende del alcance, pero la mayoría de automatizaciones se ponen en marcha en cuestión de días. Los proyectos más completos se abordan por fases para que veas resultados desde el principio.',
    },
    {
      q: '¿Se integra con las herramientas que ya uso?',
      a: 'Sí. Trabajamos sobre tu stack actual (CRM, ERP, email, mensajería, hojas de cálculo o software de gestión) conectando las piezas para que la información fluya sin trabajo manual.',
    },
    {
      q: '¿Qué pasa después de la entrega?',
      a: 'Ofrecemos soporte y mejora continua. Revisamos el rendimiento de las automatizaciones y las ajustamos a medida que tu empresa evoluciona.',
    },
  ];

  return (
    <section id="faq" ref={ref} className="py-20 md:py-32 px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50/30">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <p className="text-sm text-blue-600 font-semibold mb-4 tracking-wide uppercase">Preguntas Frecuentes</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">Resolvemos tus dudas</h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="space-y-4"
        >
          {faqs.map((item, index) => (
            <FAQItem
              key={index}
              item={item}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onBookConsultation }) => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const year = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-10 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid gap-10 md:gap-12 md:grid-cols-2 lg:grid-cols-4 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img src="/aivanilo-logo.png" alt="Aivanilo" className="h-8 w-auto mb-4 brightness-0 invert" width="180" height="40" loading="lazy" decoding="async" />
            <p className="text-slate-400 text-sm leading-relaxed">
              Automatización de procesos e inteligencia artificial para empresas que quieren crecer.
            </p>
          </div>

          {/* Navegación */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-slate-300">Navegación</h4>
            <ul className="space-y-3 text-slate-400 text-sm">
              <li><button onClick={() => scrollTo('services')} className="hover:text-white transition-colors">Servicios</button></li>
              <li><button onClick={() => scrollTo('process')} className="hover:text-white transition-colors">Proceso</button></li>
              <li><button onClick={() => scrollTo('industries')} className="hover:text-white transition-colors">Sectores</button></li>
              <li><button onClick={() => scrollTo('faq')} className="hover:text-white transition-colors">Preguntas frecuentes</button></li>
            </ul>
          </div>

          {/* Soluciones */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-slate-300">Soluciones</h4>
            <ul className="space-y-3 text-slate-400 text-sm">
              <li>Agentes de IA</li>
              <li>Automatización de procesos</li>
              <li>Integraciones</li>
              <li>Soluciones a medida</li>
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide text-slate-300">Contacto</h4>
            <a href="mailto:aivaniloinfo@gmail.com" className="text-slate-400 text-sm hover:text-white transition-colors block mb-4">
              aivaniloinfo@gmail.com
            </a>
            <button
              onClick={onBookConsultation}
              data-testid="footer-book-btn"
              className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-blue-50 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors mb-5"
            >
              Reservar consulta <ArrowRight className="w-4 h-4" />
            </button>
            <div className="flex gap-3">
              <a href="mailto:aivaniloinfo@gmail.com" aria-label="Enviar email" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Mail className="w-5 h-5" />
              </a>
              <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© {year} Aivanilo. Todos los derechos reservados.</p>
          <div className="flex gap-6 text-slate-500 text-sm">
            <a href="/privacidad" className="hover:text-slate-300 transition-colors" data-testid="footer-privacy-link">Privacidad</a>
            <a href="/privacidad" className="hover:text-slate-300 transition-colors">Términos</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default HomePage;